import { useCallback, useSyncExternalStore } from 'react';

import { MOTION_PREFERENCE_KEY } from '@/constants';
import { useMediaQuery } from './use-media-query';

/**
 * What the reader chose, persisted in localStorage. `system` follows the
 * OS "Reduce motion" setting; `reduce` and `full` override it either way.
 */
export type MotionPreference = 'system' | 'reduce' | 'full';

const listeners = new Set<() => void>();

function read(): MotionPreference {
	try {
		const stored = localStorage.getItem(MOTION_PREFERENCE_KEY);
		if (stored === 'system' || stored === 'reduce' || stored === 'full') {
			return stored;
		}
	} catch {
		// Private mode etc.: fall through to the default.
	}
	return 'system';
}

// In memory, written through to localStorage: a failed write (private mode)
// still applies the choice for this session.
let current: MotionPreference | undefined;

function getMotionPreference(): MotionPreference {
	current ??= read();
	return current;
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * The saved motion choice and its setter. A module-level store rather than
 * AppContext state, so hooks that must work without the app's providers
 * (`useViewTransition`) resolve the same value as everything else.
 */
export function useMotionPreference(): {
	motionPreference: MotionPreference;
	setMotionPreference: (preference: MotionPreference) => void;
} {
	const motionPreference = useSyncExternalStore<MotionPreference>(
		subscribe,
		getMotionPreference,
		() => 'system',
	);
	const setMotionPreference = useCallback((next: MotionPreference) => {
		current = next;
		try {
			localStorage.setItem(MOTION_PREFERENCE_KEY, next);
		} catch {
			// Private mode etc.: still apply it for this session.
		}
		listeners.forEach((listener) => listener());
	}, []);
	return { motionPreference, setMotionPreference };
}

/**
 * Whether motion should be reduced right now: the reader's choice, or the
 * OS setting (kept live) when they chose `system`.
 *
 * App mirrors this onto `<html data-motion>`, which the CSS selects on
 * instead of `@media (prefers-reduced-motion)` -- a media query can't
 * express a choice made inside the app. index-template.ejs sets the
 * attribute before first paint with the same rule; keep the two in sync.
 */
export function useReducedMotion(): boolean {
	const { motionPreference } = useMotionPreference();
	const osPrefersReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
	return motionPreference === 'system'
		? osPrefersReduced
		: motionPreference === 'reduce';
}
